import React, { useState, useEffect } from 'react';
import { X, Save, Building } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const DepartmentForm = ({ viewOnly = false }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [formData, setFormData] = useState({
    name: '',
    parent_id: '',
    description: '',
    sort_order: 0,
  });
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadDepartment = async (deptId) => {
    try {
      const res = await fetch(`/api/organization/departments/${deptId}/edit`);
      const data = await res.json();
      if (data.status === 'success') {
        setFormData({
          name: data.data.name || '',
          parent_id: data.data.parent_id ? String(data.data.parent_id) : '',
          description: data.data.description || '',
          sort_order: data.data.sort_order || 0,
        });
      }
    } catch (err) {
      console.error("Failed to load department:", err);
    }
  };

  const loadDepartments = async () => {
    try {
      const res = await fetch('/api/organization/departments');
      const data = await res.json();
      if (data.status === 'success') {
        setDepartments(data.data);
      }
    } catch (err) {
      console.error("Failed to load departments:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadDepartments();
      if (id) {
        await loadDepartment(id);
      }
      setLoading(false);
    };
    init();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (viewOnly) { navigate('/organization'); return; }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : null,
        sort_order: parseInt(formData.sort_order) || 0,
      };

      if (id) {
        // Use the view endpoint then update
        const res = await fetch(`/api/organization/departments/${id}/edit`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.status === 'success') {
          notify(t('common.success'), 'success');
          navigate('/organization');
        } else {
          notify(data.message || t('common.error'), 'error');
        }
      } else {
        const res = await fetch('/api/organization/departments/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.status === 'success') {
          notify(t('common.success'), 'success');
          navigate('/organization');
        } else {
          notify(data.message || t('common.error'), 'error');
        }
      }
    } catch (err) {
      notify(t('common.error'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="text-center p-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Building className="text-blue-600" />
          {viewOnly ? t('departments.view') : (id ? t('departments.edit') : t('departments.add'))}
        </h1>
        <button onClick={() => navigate('/organization')} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('departments.name')} <span className="text-red-500">*</span>
          </label>
          <input
            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('departments.name_placeholder')}
            required
            disabled={viewOnly}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('departments.parent')}</label>
          <select
            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
            value={formData.parent_id}
            onChange={e => setFormData({ ...formData, parent_id: e.target.value })}
            disabled={viewOnly}
          >
            <option value="">{t('departments.root_parent')}</option>
            {departments
              .filter(d => !id || d.id !== parseInt(id))
              .map(d => (
                <option key={d.id} value={String(d.id)}>{d.name}</option>
              ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('departments.sort_order')}</label>
          <input
            type="number"
            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
            value={formData.sort_order}
            onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
            min="0"
            disabled={viewOnly}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('departments.description')}</label>
          <textarea
            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('departments.desc_placeholder')}
            rows={3}
            disabled={viewOnly}
          />
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-slate-200 dark:border-slate-800">
          {viewOnly ? (
            <button type="button" onClick={() => navigate('/organization')} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              {t('common.close')}
            </button>
          ) : (
            <>
              <button type="button" onClick={() => navigate('/organization')} className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                {t('common.cancel')}
              </button>
              <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('common.processing')}
                  </span>
                ) : (
                  <><Save size={18} />{id ? t('common.update') : t('common.create')}</>
                )}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default DepartmentForm;
